import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSearchParams, useParams } from 'react-router-dom';
import axios from 'axios';
import { ShoppingCart, Pencil, Search, X, Trash2, ArrowLeft, Check, Printer } from 'lucide-react';
import { useEditDocument, EditabilityBanner, saveErrorMessage } from '../nativerecords/documentEdit';
import { openPdf } from '../nativerecords/DocumentActions';
import {
  isWeightItem, isWeightLine, quantityUnit, costUnit,
  poundsToBaseOunces, baseOuncesToPounds, costPerPoundToBase, costPerBaseToPound,
} from './weightUnits';
import './NativePurchaseOrderNew.css';

// New Purchase Order (Native) — manual PO entry on the native platform
// (task #43). Replaces hand-typing POs into NetSuite: the PO is created as a
// DRAFT in the native purchasing tables (packages/purchasing, source
// 'manual', idempotent on a client-generated MAN-PO-… ref). The NetSuite copy
// arrives with the purchasing reverse-sync worker (not built yet) — the
// confirmation says so.
//
// Layout follows the house template (NativeOrderNew): preset cards → picker →
// line editor → review → confirmation.
//
// UNITS: weight items (fragrance oil, wax — NS units type '1') are stored in
// base OUNCES at a rate per ounce, but nobody buys oil that way: Cosmo and
// Givaudan quote per POUND with a 25 lb minimum. So every line on this screen
// is held and typed in DISPLAY units — pounds and $/lb for weight items,
// eaches otherwise — and converted to base units at the two API boundaries
// (load and submit) through weightUnits.js, which is the only place the ×16
// lives. The stored row is therefore byte-identical in shape to a mirrored NS
// line, and receiving, the oil dashboard and reverse-sync all keep reading it
// the way they always have.
//
// EDIT MODE: mounted at /native-purchase-orders/:id/edit the same screen loads
// that PO, prefills itself, and saves with PATCH. A PO posts nothing, so an
// amendment is a data change — but the server decides what may change
// (nativeDocEdit.js): once the PO is approved it drops to dates, memos, and
// quantities (never below what has been received or billed), and once the
// reverse-sync worker has made the NetSuite copy it refuses outright.

const PO_TYPES = [
  { key: 'standard', label: 'Standard', hint: 'A normal replenishment or one-off purchase.' },
  { key: 'rush', label: 'Rush', hint: 'Expedited — memo tagged RUSH, expected date defaults to next week.' },
];

const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;
const newRef = () => `MAN-PO-${Date.now()}-${Math.floor(Math.random() * 1e6)}`;
const inDays = (d) => new Date(Date.now() + d * 86400000).toISOString().slice(0, 10);

function useDebouncedSearch(fetcher, minLen = 2) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  useEffect(() => {
    if (query.trim().length < minLen) { setResults([]); return undefined; }
    const t = setTimeout(async () => {
      try {
        setSearching(true);
        setResults(await fetcher(query.trim()));
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [query, fetcher, minLen]);
  return { query, setQuery, results, setResults, searching };
}

const searchVendors = async (q) => {
  const { data } = await axios.get('/api/native-purchasing/vendors', { params: { q } });
  return data.vendors || [];
};

// Item search reuses the existing native-items endpoint (uuid + NS id + costs).
const searchItems = async (q) => {
  const { data } = await axios.get('/api/native-items', { params: { q, pageSize: 15 } });
  return data.items || [];
};

export default function NativePurchaseOrderNew() {
  const { id: editId } = useParams();
  const isEdit = Boolean(editId);
  const [step, setStep] = useState('edit'); // edit | review | done
  const [poType, setPoType] = useState('standard');
  const [vendor, setVendor] = useState(null);
  const [lines, setLines] = useState([]);
  const [expectedDate, setExpectedDate] = useState('');
  const [expectedTouched, setExpectedTouched] = useState(false);
  const [memo, setMemo] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [confirmation, setConfirmation] = useState(null);
  const externalRef = useRef(newRef());

  const vendorSearch = useDebouncedSearch(searchVendors);
  const itemSearch = useDebouncedSearch(searchItems);

  // ── edit mode ──────────────────────────────────────────────────────────────
  const [editability, setEditability] = useState(null);
  const [poNumber, setPoNumber] = useState(null);
  const expectedUpdatedAt = useRef(undefined);
  const loaded = useEditDocument(isEdit ? `/api/native-purchasing/${encodeURIComponent(editId)}` : null, (d) => d.purchaseOrder);

  useEffect(() => {
    const po = loaded.doc;
    if (!po) return;
    expectedUpdatedAt.current = po.updated_at ?? null;
    externalRef.current = po.source_key;
    setEditability(loaded.editability);
    setPoNumber(po.po_number);
    setVendor({ id: po.vendor_id, nsInternalId: null, name: po.vendor_name, email: null, paymentTerms: po.terms || null });
    // Line ids ride along: the API reconciles by id, so dropping them would
    // delete and re-create every line (and the received floors would refuse).
    setLines((po.lines || []).map((l) => {
      // Stored base units -> the pounds this screen edits in (weightUnits.js).
      const isWeight = isWeightLine(l);
      const toQty = isWeight ? baseOuncesToPounds : Number;
      const toCost = isWeight ? costPerBaseToPound : Number;
      return {
        id: l.id,
        itemId: l.inventory_item_id || null,
        nsItemId: l.item_id != null ? String(l.item_id) : null,
        sku: l.item_sku,
        name: l.item_name,
        isWeight,
        quantity: toQty(l.quantity_ordered),
        unitCost: toCost(l.unit_cost) || 0,
        costSource: null,
        expectedDate: l.expected_date ? String(l.expected_date).slice(0, 10) : '',
        quantityReceived: toQty(Math.max(Number(l.quantity_received || 0), Number(l.quantity_billed || 0))),
        hints: [],
      };
    }));
    setExpectedDate(po.expected_delivery_date ? String(po.expected_delivery_date).slice(0, 10) : '');
    setExpectedTouched(true);
    setMemo(po.memo || '');
  }, [loaded.doc, loaded.editability]);

  const locked = isEdit && editability != null && !editability.editable;
  const restricted = isEdit && editability != null && editability.mode === 'restricted';

  // Make Copy (?copyFrom=<purchase-order record id>): prefill vendor + lines
  // from the mirrored record (open quantities and last costs copy as-is).
  const [searchParams] = useSearchParams();
  const [copyNote, setCopyNote] = useState(null);
  useEffect(() => {
    const copyFrom = searchParams.get('copyFrom');
    if (!copyFrom || isEdit) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`/api/native-records/purchase-orders/${encodeURIComponent(copyFrom)}`);
        if (cancelled) return;
        const rec = data.record || {};
        if (rec.vendor_name) {
          try {
            const { data: vs } = await axios.get('/api/native-purchasing/vendors', { params: { q: rec.vendor_name } });
            const cands = vs.vendors || [];
            const found = cands.find((v) => rec.vendor_id != null && String(v.nsInternalId) === String(rec.vendor_id)) || cands[0];
            if (found && !cancelled) setVendor(found);
          } catch { /* vendor stays unset — operator picks */ }
        }
        const mapped = [];
        for (const l of data.lines || []) {
          if (!(Number(l.quantity_ordered) > 0) || (!l.inventory_item_id && l.item_id == null)) continue;
          let itemId = l.inventory_item_id || null;
          let sku = l.item_sku;
          let name = l.item_name;
          if (!itemId && l.item_id != null) {
            // older mirror lines predate inventory_item_id — resolve via NS id
            try {
              const { data: it } = await axios.get(`/api/native-items/${encodeURIComponent(l.item_id)}`);
              itemId = it.item?.id || null;
              sku = it.item?.sku || sku;
              name = it.item?.name || name;
            } catch { /* skip unresolvable lines below */ }
          }
          if (!itemId) continue;
          // Same base-units -> pounds conversion as edit mode. The source line
          // may be an NS-mirror row, which carries no unit_of_measure — the SKU
          // prefix is what identifies it (weightUnits.isWeightLine).
          const isWeight = isWeightLine({ ...l, item_sku: sku });
          mapped.push({
            itemId,
            nsItemId: l.item_id != null ? String(l.item_id) : null,
            sku,
            name,
            isWeight,
            quantity: isWeight ? baseOuncesToPounds(l.quantity_ordered) : Number(l.quantity_ordered),
            unitCost: (isWeight ? costPerBaseToPound(l.unit_cost) : Number(l.unit_cost)) || 0,
            costSource: 'copied from source PO',
            expectedDate: '',
            hints: [],
          });
        }
        if (cancelled) return;
        setLines(mapped);
        setMemo(`Copy of ${rec.order_number || rec.po_number || copyFrom}`);
        setCopyNote(`Copied ${mapped.length} line${mapped.length === 1 ? '' : 's'} from ${rec.order_number || rec.po_number || 'the source PO'} — review vendor, quantities, and costs before posting.`);
      } catch (err) {
        if (!cancelled) setCopyNote(`Could not copy from the source PO: ${err.response?.data?.error || err.message}`);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Rush POs default the expected date to a week out (until touched).
  useEffect(() => {
    if (poType === 'rush' && !expectedTouched) setExpectedDate(inDays(7));
  }, [poType, expectedTouched]);

  const subtotal = useMemo(
    () => lines.reduce((s, l) => s + (Number(l.unitCost) || 0) * (Number(l.quantity) || 0), 0),
    [lines]
  );

  const addItem = async (item) => {
    const index = lines.length;
    // Weight items are typed in pounds; item.currentCost is per base OUNCE.
    const isWeight = isWeightItem(item);
    setLines((prev) => [...prev, {
      itemId: item.id,            // inventory_items uuid
      nsItemId: item.netsuiteId,  // NS internal id (mirror line convention)
      sku: item.sku,
      name: item.name,
      isWeight,
      quantity: 1,
      unitCost: item.currentCost != null
        ? (isWeight ? costPerBaseToPound(item.currentCost) : item.currentCost)
        : 0,
      costSource: item.currentCost != null ? `item cost${isWeight ? ' (per lb)' : ''}` : null,
      expectedDate: '',
      hints: [],
    }]);
    itemSearch.setQuery('');
    itemSearch.setResults([]);

    // item_vendors purchasing facts: preferred-vendor hint + last-cost default.
    try {
      const { data } = await axios.get('/api/native-purchasing/item-vendors', { params: { itemId: item.id } });
      const hints = data.vendors || [];
      if (hints.length === 0) return;
      setLines((prev) => prev.map((l, i) => {
        if (i !== index) return l;
        const match =
          (vendor && hints.find((h) => h.vendorNsInternalId != null && String(h.vendorNsInternalId) === String(vendor.nsInternalId))) ||
          hints.find((h) => h.isPreferred) ||
          hints[0];
        const useCost = match && match.lastUnitCost != null;
        // item_vendors.last_unit_cost is a PO line rate — per base ounce on a
        // weight item, so it needs the same ×16 the item cost got.
        const lastCost = useCost && l.isWeight ? costPerBaseToPound(match.lastUnitCost) : match?.lastUnitCost;
        return {
          ...l,
          hints,
          unitCost: useCost ? lastCost : l.unitCost,
          costSource: useCost
            ? `last cost from ${match.vendorName || 'vendor history'}${l.isWeight ? ' (per lb)' : ''}`
            : l.costSource,
        };
      }));
    } catch {
      // hints are best-effort — the line stands without them
    }
  };

  const updateLine = (i, patch) => setLines((prev) => prev.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const removeLine = (i) => setLines((prev) => prev.filter((_, idx) => idx !== i));

  // Preferred-vendor hints for the lines (dedupe by vendor).
  const vendorHints = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const l of lines) {
      const h = (l.hints || []).find((x) => x.isPreferred) || (l.hints || [])[0];
      if (!h || !h.vendorName) continue;
      const key = `${h.vendorNsInternalId ?? h.vendorName}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({ ...h, sku: l.sku });
    }
    return out;
  }, [lines]);

  const applyHintVendor = (h) => {
    setVendor({ id: null, nsInternalId: h.vendorNsInternalId, name: h.vendorName, email: null, paymentTerms: null });
  };

  const validate = () => {
    if (!vendor) return 'Pick a vendor first.';
    if (lines.length === 0) return 'Add at least one line item.';
    for (const l of lines) {
      if (!(Number(l.quantity) > 0)) return `Quantity for ${l.sku || l.name} must be greater than 0.`;
      if (Number(l.unitCost) < 0 || !Number.isFinite(Number(l.unitCost))) return `Unit cost for ${l.sku || l.name} must be 0 or more.`;
    }
    return null;
  };

  const goReview = () => {
    const problem = validate();
    if (problem) { setError(problem); return; }
    setError(null);
    setStep('review');
  };

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const body = {
        externalRef: externalRef.current,
        vendor: { id: vendor.id, nsInternalId: vendor.nsInternalId, name: vendor.name },
        terms: vendor.paymentTerms || null,
        expectedDate: expectedDate || null,
        memo: [poType === 'rush' ? 'RUSH' : null, memo.trim() || null].filter(Boolean).join(' — ') || null,
        lines: lines.map((l) => ({
          id: l.id,                       // kept lines are matched by id
          itemId: l.itemId,
          nsItemId: l.nsItemId,
          sku: l.sku,
          name: l.name,
          // Display pounds -> stored base ounces (weightUnits.js). The line
          // lands in exactly the shape an NS-mirror row has, so receiving reads
          // it as the stock quantity it already is.
          //
          // is_oil_item is deliberately left FALSE, matching every mirrored oil
          // line: packages/purchasing still reads the ÷16 rule the old way and
          // demands a receipt carry a stockQuantity DIFFERENT from the ordered
          // quantity, which for these items (purchase_uom_id === stock_uom_id)
          // can never be true. Setting the flag would fail the PO-linkage leg
          // of the first receipt. The receiving screen still labels the line by
          // unit_of_measure and the item's units_type.
          quantity: l.isWeight ? poundsToBaseOunces(l.quantity) : Number(l.quantity),
          unitCost: l.isWeight ? costPerPoundToBase(l.unitCost) : Number(l.unitCost),
          unitOfMeasure: l.isWeight ? 'oz' : null,
          expectedDate: l.expectedDate || null,
        })),
      };
      const { data } = isEdit
        ? await axios.patch(`/api/native-purchasing/${encodeURIComponent(editId)}`, { ...body, expectedUpdatedAt: expectedUpdatedAt.current })
        : await axios.post('/api/native-purchasing', body);
      if (isEdit) {
        // Re-arm the compare-and-swap so a second save from this screen works.
        expectedUpdatedAt.current = data.purchaseOrder?.updated_at ?? null;
        setEditability(data.editability || editability);
      }
      setConfirmation(data);
      setStep('done');
    } catch (err) {
      setError(saveErrorMessage(err));
      setStep('edit');
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    externalRef.current = newRef();
    setStep('edit');
    setPoType('standard');
    setVendor(null);
    setLines([]);
    setExpectedDate('');
    setExpectedTouched(false);
    setMemo('');
    setConfirmation(null);
    setError(null);
  };

  // ── confirmation ───────────────────────────────────────────────────────────
  if (step === 'done' && confirmation) {
    const po = confirmation.purchaseOrder;
    return (
      <div className="npo-page">
        <div className="npo-card npo-confirm">
          <div className="npo-confirm-icon"><Check size={28} /></div>
          <h2>{isEdit ? 'Purchase order updated' : 'Purchase order created'}</h2>
          <div className="npo-confirm-grid">
            <span>PO number</span><code>{po.po_number}</code>
            <span>Native PO id</span><code>{po.id}</code>
            <span>Vendor</span><strong>{po.vendor_name}</strong>
            <span>Lifecycle</span><strong>{po.lifecycle_status}</strong>
            <span>Total</span><strong>{money(po.total_amount)}</strong>
          </div>
          <p className="npo-note">{confirmation.netsuiteNote || confirmation.note}</p>
          <div className="npo-actions">
            {/* The branded PO PDF (packages/documents) — letterhead, vendor and
                ship-to blocks, lines with their unit, terms, notes. Opens in a
                new tab to print or save for the vendor. */}
            <button className="npo-btn npo-btn-ghost" onClick={() => openPdf('purchase-order', po.id)}>
              <Printer size={15} /> Print PO for the vendor
            </button>
            {isEdit ? (
              <button className="npo-btn npo-btn-primary" onClick={() => setStep('edit')}>Back to the purchase order</button>
            ) : (
              <>
                <a className="npo-btn npo-btn-ghost" href={`/native-purchase-orders/${po.id}/edit`}>Edit this PO</a>
                <button className="npo-btn npo-btn-primary" onClick={reset}>Create another purchase order</button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── review ─────────────────────────────────────────────────────────────────
  if (step === 'review') {
    return (
      <div className="npo-page">
        <div className="npo-header">
          <button className="npo-btn npo-btn-ghost" onClick={() => setStep('edit')}><ArrowLeft size={16} /> Back to edit</button>
          <h1>{isEdit ? `Review changes to ${poNumber || 'this PO'}` : 'Review purchase order'}</h1>
        </div>
        {error && <div className="npo-error">{error}</div>}
        <div className="npo-card">
          <div className="npo-review-row"><span>Type</span><strong>{PO_TYPES.find((t) => t.key === poType)?.label}</strong></div>
          <div className="npo-review-row">
            <span>Vendor</span><strong>{vendor?.name}</strong>
            {vendor?.email && <em>{vendor.email}</em>}
            {vendor?.nsInternalId != null && <code>NS {vendor.nsInternalId}</code>}
          </div>
          {expectedDate && <div className="npo-review-row"><span>Expected</span><strong>{expectedDate}</strong></div>}
          <table className="npo-table">
            <thead><tr><th>SKU</th><th>Item</th><th className="r">Qty</th><th className="r">Unit cost</th><th>Expected</th><th className="r">Amount</th></tr></thead>
            <tbody>
              {lines.map((l, i) => (
                <tr key={i}>
                  <td><code>{l.sku}</code></td>
                  <td>{l.name}</td>
                  <td className="r">{l.quantity} {quantityUnit(l.isWeight)}</td>
                  <td className="r">{money(l.unitCost)}{costUnit(l.isWeight)}</td>
                  <td>{l.expectedDate || '—'}</td>
                  <td className="r">{money((Number(l.unitCost) || 0) * (Number(l.quantity) || 0))}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="npo-totals">
            <div className="npo-total-line"><span>Total</span><strong>{money(subtotal)}</strong></div>
          </div>
          {memo && <div className="npo-review-row"><span>Memo</span><em>{poType === 'rush' ? `RUSH — ${memo}` : memo}</em></div>}
          {!memo && poType === 'rush' && <div className="npo-review-row"><span>Memo</span><em>RUSH</em></div>}
          <p className="npo-hint">
            {isEdit
              ? 'Saves in place — a purchase order posts nothing, so there is no reversal and nothing is sent to the vendor from this screen.'
              : 'Creates a native DRAFT purchase order — nothing is sent to the vendor or to NetSuite from this screen.'}
          </p>
          <div className="npo-actions">
            <button className="npo-btn npo-btn-ghost" onClick={() => setStep('edit')}>Back</button>
            <button className="npo-btn npo-btn-primary" disabled={submitting || locked} onClick={submit}>
              {submitting ? 'Saving…' : (isEdit ? 'Save changes' : 'Create purchase order')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── edit ───────────────────────────────────────────────────────────────────
  return (
    <div className="npo-page">
      <div className="npo-header">
        {isEdit ? (
          <>
            <h1><Pencil size={22} /> Edit Purchase Order {poNumber || ''}</h1>
            <p className="npo-sub">A purchase order posts nothing, so changes save in place — no reversal, no re-entry.</p>
          </>
        ) : (
          <>
            <h1><ShoppingCart size={22} /> New Purchase Order (Native)</h1>
            <p className="npo-sub">Manual purchase order on the native platform — created as a draft, no NetSuite typing.</p>
          </>
        )}
      </div>
      {loaded.loading && <div className="npo-card"><p className="npo-sub" style={{ margin: 0 }}>Loading the purchase order…</p></div>}
      {loaded.error && <div className="npo-error">{loaded.error}</div>}
      <EditabilityBanner editability={editability} prefix="npo" />
      {copyNote && <div className="npo-card"><p className="npo-sub" style={{ margin: 0 }}>{copyNote}</p></div>}
      {error && <div className="npo-error">{error}</div>}

      <div className="npo-card">
        <h3>Order type</h3>
        <div className="npo-presets">
          {PO_TYPES.map((t) => (
            <button
              key={t.key}
              className={`npo-preset ${poType === t.key ? 'active' : ''}`}
              disabled={isEdit}
              onClick={() => setPoType(t.key)}
            >
              <strong>{t.label}</strong>
              <span>{t.hint}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="npo-card">
        <h3>Vendor</h3>
        {vendor ? (
          <div className="npo-chip">
            <div>
              <strong>{vendor.name}</strong>
              {vendor.email && <em>{vendor.email}</em>}
              {vendor.nsInternalId != null && <code>NS {vendor.nsInternalId}</code>}
              {vendor.paymentTerms && <span className="npo-tag">{vendor.paymentTerms}</span>}
            </div>
            {!locked && !restricted && (
              <button className="npo-icon-btn" onClick={() => setVendor(null)} title="Change vendor"><X size={16} /></button>
            )}
          </div>
        ) : (
          <div className="npo-search">
            <Search size={16} className="npo-search-icon" />
            <input
              className="npo-input npo-input-search"
              placeholder="Search vendors by name or email…"
              value={vendorSearch.query}
              onChange={(e) => vendorSearch.setQuery(e.target.value)}
            />
            {(vendorSearch.results.length > 0 || vendorSearch.searching) && (
              <div className="npo-results">
                {vendorSearch.searching && <div className="npo-result-muted">Searching…</div>}
                {vendorSearch.results.map((v) => (
                  <button key={v.id} className="npo-result" onClick={() => { setVendor(v); vendorSearch.setQuery(''); vendorSearch.setResults([]); }}>
                    <strong>{v.name}</strong>
                    {v.email && <em>{v.email}</em>}
                    {v.paymentTerms && <span className="npo-tag">{v.paymentTerms}</span>}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        {vendorHints.length > 0 && (
          <div className="npo-hints">
            {vendorHints.map((h, i) => (
              <div key={i} className="npo-hint npo-hint-row">
                <span>
                  Preferred vendor for <code>{h.sku}</code>: <strong>{h.vendorName}</strong>
                  {h.lastUnitCost != null && <> — last cost {money(h.lastUnitCost)}</>}
                  {h.leadTimeDays != null && <>, lead {h.leadTimeDays}d</>}
                </span>
                {!vendor && h.vendorName && (
                  <button className="npo-btn npo-btn-mini" onClick={() => applyHintVendor(h)}>Use vendor</button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="npo-card">
        <h3>Line items</h3>
        <div className="npo-search">
          <Search size={16} className="npo-search-icon" />
          <input
            className="npo-input npo-input-search"
            placeholder={restricted ? 'Lines cannot be added once the PO is approved' : 'Add item — search by SKU or name…'}
            value={itemSearch.query}
            disabled={locked || restricted}
            onChange={(e) => itemSearch.setQuery(e.target.value)}
          />
          {(itemSearch.results.length > 0 || itemSearch.searching) && (
            <div className="npo-results">
              {itemSearch.searching && <div className="npo-result-muted">Searching…</div>}
              {itemSearch.results.map((it) => (
                <button key={it.id} className="npo-result" onClick={() => addItem(it)}>
                  <code>{it.sku}</code>
                  <strong>{it.name}</strong>
                  {it.currentCost != null && <em>{money(it.currentCost)}</em>}
                </button>
              ))}
            </div>
          )}
        </div>

        {lines.length > 0 && (
          <table className="npo-table">
            <thead><tr><th>SKU</th><th>Item</th><th className="r">Qty</th><th className="r">Unit cost</th><th>Expected</th><th className="r">Amount</th><th /></tr></thead>
            <tbody>
              {lines.map((l, i) => (
                <tr key={l.id || i}>
                  <td><code>{l.sku}</code></td>
                  <td>
                    {l.name}
                    {l.isWeight && <div className="npo-cost-source">bought by weight — enter pounds and price per pound</div>}
                    {l.costSource && <div className="npo-cost-source">cost default: {l.costSource}</div>}
                    {l.quantityReceived > 0 && (
                      <div className="npo-cost-source">{l.quantityReceived} {quantityUnit(l.isWeight)} already received or billed — this line cannot go below that.</div>
                    )}
                  </td>
                  <td className="r">
                    <span className="npo-unit-field">
                      <input type="number" min={l.quantityReceived || 0} step={l.isWeight ? 'any' : '1'} className="npo-input npo-input-num" value={l.quantity} disabled={locked} onChange={(e) => updateLine(i, { quantity: e.target.value })} />
                      <span className="npo-unit">{quantityUnit(l.isWeight)}</span>
                    </span>
                  </td>
                  <td className="r">
                    <span className="npo-unit-field">
                      <input type="number" min="0" step="0.01" className="npo-input npo-input-num" value={l.unitCost} disabled={locked || restricted} onChange={(e) => updateLine(i, { unitCost: e.target.value, costSource: null })} />
                      <span className="npo-unit">{costUnit(l.isWeight)}</span>
                    </span>
                  </td>
                  <td><input type="date" className="npo-input npo-input-date" value={l.expectedDate} disabled={locked} onChange={(e) => updateLine(i, { expectedDate: e.target.value })} /></td>
                  <td className="r">{money((Number(l.unitCost) || 0) * (Number(l.quantity) || 0))}</td>
                  <td>
                    {!locked && !restricted && (
                      <button className="npo-icon-btn" onClick={() => removeLine(i)} title="Remove line"><Trash2 size={15} /></button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="npo-card">
        <h3>Dates and notes</h3>
        <div className="npo-form-grid">
          <label>
            Expected delivery
            <input type="date" className="npo-input" value={expectedDate} disabled={locked} onChange={(e) => { setExpectedDate(e.target.value); setExpectedTouched(true); }} />
          </label>
        </div>
        <label className="npo-label-block">
          Memo
          <textarea className="npo-input" rows={2} placeholder="Optional note — appears on the PO" value={memo} disabled={locked} onChange={(e) => setMemo(e.target.value)} />
        </label>
      </div>

      <div className="npo-footer">
        <div className="npo-footer-totals">
          <span>{lines.length} line{lines.length === 1 ? '' : 's'}</span>
          <strong>Total {money(subtotal)}</strong>
        </div>
        <button className="npo-btn npo-btn-primary" onClick={goReview} disabled={!vendor || lines.length === 0 || locked}>
          {isEdit ? 'Review changes' : 'Review purchase order'}
        </button>
      </div>
    </div>
  );
}
